// ============================================================================
// Customer Service
// Fetches customer data from the admin-backend API
// ============================================================================

import { fetchWithAuth } from './auth';
import { Customer } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

// ============================================================================
// API Response Types (match backend Pydantic schemas — snake_case)
// ============================================================================

export interface ApiCustomer {
  id: number;
  store_id: number;
  first_name: string;
  last_name: string;
  email: string;
  main_phone?: string;
  second_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  banned: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
}

export interface CustomerCreate {
  first_name: string;
  last_name: string;
  email: string;
  main_phone?: string;
  second_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  banned?: boolean;
}

export interface CustomerUpdate {
  first_name?: string;
  last_name?: string;
  email?: string;
  main_phone?: string;
  second_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  banned?: boolean;
}

// ============================================================================
// API Functions
// ============================================================================

export async function getCustomers(storeId?: number): Promise<ApiCustomer[]> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  const qs = params.toString();
  return fetchWithAuth(`${API_BASE_URL}/customers/${qs ? '?' + qs : ''}`);
}

export async function getCustomerById(customerId: number): Promise<ApiCustomer> {
  return fetchWithAuth(`${API_BASE_URL}/customers/${customerId}`);
}

export async function getCustomersByStoreAndEmail(storeId: number, email: string): Promise<ApiCustomer[]> {
  const customers = await getCustomers(storeId);
  return customers.filter(c => c.email.toLowerCase() === email.toLowerCase());
}

export async function createCustomer(data: CustomerCreate, storeId?: number): Promise<ApiCustomer> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  const qs = params.toString();
  return fetchWithAuth(`${API_BASE_URL}/customers/${qs ? '?' + qs : ''}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCustomer(customerId: number, data: CustomerUpdate): Promise<ApiCustomer> {
  return fetchWithAuth(`${API_BASE_URL}/customers/${customerId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCustomer(customerId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/customers/${customerId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Mapper: API Response → Frontend Type
// ============================================================================

export function mapApiCustomer(api: ApiCustomer): Customer {
  return {
    id: api.id,
    storeId: api.store_id,
    firstName: api.first_name,
    lastName: api.last_name,
    email: api.email,
    mainPhone: api.main_phone,
    secondPhone: api.second_phone,
    address: api.address,
    city: api.city,
    state: api.state,
    zipCode: api.zip_code,
    country: api.country,
    banned: api.banned,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}
